import { Component, OnInit } from '@angular/core';
import { FormControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbActiveModal, NgbTypeaheadSelectItemEvent } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { debounceTime, lastValueFrom, Observable, OperatorFunction, switchMap } from 'rxjs';
import { CannotContainOnlySpaceValidator } from 'src/app/directives/cannot-contain-only-space-validator.directive';
import { OnlyLettersAndNumbersValidator } from 'src/app/directives/only-letters-and-numbers-validator.directive';
import { FindUserModel } from 'src/app/models/user';
import { WorkgroupList } from 'src/app/models/workgroup-information.model';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { IdentityUsersService } from 'src/app/services/identityUsers.service';
import { IdentityWorkgroupsService } from 'src/app/services/identityWorkgroups.service';
import { LoggingService } from 'src/app/services/logging.service';
import { PlatformHubService } from 'src/app/services/platformhub.service';
import { ToastService } from 'src/app/services/toast.service';

@Component({
  selector: 'app-add-edit-workgroup',
  templateUrl: './add-edit-workgroup.component.html'
})
export class AddEditWorkgroupComponent implements OnInit {
  action!: string;
  organisationGuid!: string;
  translations = <any>{};
  workgroupGuid!: string;
  workgroupName!: string;
  workgroupMembers: any[] = [];
  workgroupList!: WorkgroupList[];
  workgroupNameField!: string;
  usingClearancelevel: boolean = true;
  workgroupAlreadyExist: boolean = false;
  selectedUser!: FindUserModel | null;

  form: UntypedFormGroup;
  workgroupNameControl: FormControl;

  search: OperatorFunction<string, FindUserModel[]> = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(300),
      switchMap((term) =>
        term.length < 2 ? [] : this.findUsers(term)
      ),
    );

  constructor(private toastService: ToastService, private translateService: TranslateService, private activeModal: NgbActiveModal,
    private workgroupService: IdentityWorkgroupsService, private formBuilder: UntypedFormBuilder, private usersService: IdentityUsersService,
    private loggingService: LoggingService, private authenticationService: AuthenticationService, private platformHub: PlatformHubService) {

    this.workgroupNameControl = new FormControl('', [Validators.required, Validators.maxLength(20), OnlyLettersAndNumbersValidator(), CannotContainOnlySpaceValidator()]);
    this.form = this.formBuilder.group(
      {
        name: this.workgroupNameControl
      });

    this.getTranslations();
    translateService.onLangChange.subscribe(() => {
      this.getTranslations();
    })
  }

  getTranslations() {
    this.translateService.get([
      'Toasts.AddWorkgroupError',
      'Toasts.EditWorkgroupError',
      'Toasts.RemoveMemberError',
      'Toasts.Error',
      'Toasts.Created',
      'Toasts.CreateWorkgroupHeader',
      'Toasts.NoUserSelected'
    ]).subscribe(translation => {
      this.translations.toastsAddWorkgroupError = translation['Toasts.AddWorkgroupError'];
      this.translations.toastsEditWorkgroupError = translation['Toasts.EditWorkgroupError'];
      this.translations.toastsRemoveMemberError = translation['Toasts.RemoveMemberError'];
      this.translations.error = translation['Toasts.Error'];
      this.translations.toastCreateWorkgroupHeader = translation['Toasts.CreateWorkgroupHeader'];
      this.translations.toastCreated = translation['Toasts.Created'];
      this.translations.noUserSelected = translation['Toasts.NoUserSelected'];
    });
  }

  ngOnInit(): void {
    this.workgroupNameControl.setValue(this.workgroupName);
    if (this.workgroupMembers && this.workgroupMembers.every((s: { clearanceLevel: number }) => s.clearanceLevel === 1)) this.usingClearancelevel = false;
  }

  async findUsers(term: string) {
    let findUsers = await this.usersService.findUsersSafeSearchValue(this.organisationGuid, term);
    let users = await lastValueFrom(findUsers);

    let returnUsers = users.filter(u => !this.workgroupMembers.map(m => m.user.id).includes(u.id));
    return returnUsers;
  }

  close() {
    this.activeModal.close(false);
  }

  async addWorkgroup() {
    this.workgroupNameField = this.workgroupNameControl.value;
    this.workgroupName = this.workgroupNameField;
    (await this.workgroupService.createWorkgroup(this.organisationGuid, this.workgroupNameField)).subscribe({
      error: () => {
        this.toastService.showError("" + this.translations.error + "", "" + this.translations.toastsAddWorkgroupError + "");
      },
      next: (data: any) => {
        this.action = "edit";
        this.workgroupGuid = data;
        this.toastService.showSuccess(this.translations.toastCreated, this.translations.toastCreateWorkgroupHeader);
      }
    });
  }

  async editWorkgroup() {
    this.workgroupNameField = this.workgroupNameControl.value;
    if (this.workgroupName !== undefined && this.workgroupName !== this.workgroupNameField) {
      try {
        let editWorkgroup = await this.workgroupService.editWorkgroup(this.organisationGuid, this.workgroupGuid, this.workgroupNameField);
        await lastValueFrom(editWorkgroup);
        this.action = "edit";
        let updateMembers = await this.workgroupService.updateMembersWorkgroup(this.organisationGuid, this.workgroupGuid, this.workgroupMembers);
        await lastValueFrom(updateMembers);
        this.toastService.showSaved();
        this.close();
      } catch (error: any) {
        this.toastService.showError("" + this.translations.error + "", "" + this.translations.toastsEditWorkgroupError + "");
      }
    } else if (this.workgroupName !== undefined && this.workgroupName === this.workgroupNameField) {
      try {
        let updateMembersGroup = await this.workgroupService.updateMembersWorkgroup(this.organisationGuid, this.workgroupGuid, this.workgroupMembers);
        await lastValueFrom(updateMembersGroup);
        this.toastService.showSaved();
        this.close();
      } catch (error: any) {
        this.toastService.showError("" + this.translations.error + "", "" + this.translations.toastsEditWorkgroupError + "");
      }
    } else {
      this.action = "edit";
      this.close();
    }
  }

  verifyExistingWorkgroup() {
    const formField = document.getElementById("name");
    this.workgroupAlreadyExist = false;
    if (!formField) {
      return;
    }

    // we must have a list of existing workgroups
    // we must have a value in the contorl
    // the control must have been changed since the start (to not find itself)
    // the value must not exists in the list
    let givenNameValue = this.workgroupNameControl.value;
    let verifyName = givenNameValue.toLowerCase().trim();
    if (this.workgroupList && this.workgroupNameControl.value && this.workgroupName != givenNameValue && this.workgroupList.includes(verifyName)) {
      formField.classList.add('is-invalid');
      this.workgroupAlreadyExist = true;
      return;
    }
    formField.classList.remove('is-invalid');
    return;
  }

  getWorkgroup() {
    this.workgroupService.getWorkgroup(this.organisationGuid, this.workgroupGuid)
      .then(value => {
        lastValueFrom(value)
          .then((data: any) => this.workgroupMembers = data.workgroupMembers)
          .catch((error) => this.loggingService.logException(error));
      })
      .catch((error) => this.loggingService.logException(error));
  }

  async TriggerAction(event: any) {
    if (event.action === "removeMemberWorkgroup" && event.id) {
      await this.removeMemberWorkgroup(event.id);
    }
  }

  async removeMemberWorkgroup(userId: string) {
    (await this.workgroupService.deleteMemberWorkgroup(this.organisationGuid, this.workgroupGuid, userId)).subscribe({
      error: () => {
        this.toastService.showError("" + this.translations.error + "", "" + this.translations.toastsRemoveMemberError + "");
        this.getWorkgroup();
      },
      next: () => {
        if (userId === this.authenticationService.currentUserValue.sub) {
          let index = this.authenticationService.workgroups.findIndex(p => p.Guid == this.workgroupGuid)
          this.authenticationService.workgroups.splice(index, index);
          this.authenticationService.forceNewToken().subscribe((data) => {
            this.platformHub.start(true);
          })
        }
        this.getWorkgroup();
      }
    });
  }

  changeUsingClearancelevel(event: any) {
    this.usingClearancelevel = event.target!.checked;
    if (this.usingClearancelevel === false) {
      this.workgroupMembers.forEach((workgroupMember: { clearanceLevel: number }) => {
        workgroupMember.clearanceLevel = 1;
      });
    }
  }

  async prefillMember(event: NgbTypeaheadSelectItemEvent) {
    event.preventDefault();
    let user: FindUserModel = event.item;
    if (user !== null) {
      if (user.voornaam === undefined) return user;
      this.selectedUser = user;
      (document.getElementById('MemberToAdd') as HTMLInputElement).value = (user.voornaam + " " + (user.tussenvoegsels != null ? user.tussenvoegsels + " " : "") + user.achternaam);
    }
    return;
  }

  async addMember() {
    if (!this.selectedUser) {
      this.toastService.showWarning(this.translations.error, this.translations.noUserSelected)
      return;
    }
    let workgroupMember = {
      OrganisationWorkgroupId: 0,
      OrganisationWorkgroup: {
        Guid: this.workgroupGuid
      },
      UserId: this.selectedUser!.id,
      ClearanceLevel: 1
    };
    (await this.workgroupService.addMemberToWorkgroup(this.organisationGuid, this.workgroupGuid, workgroupMember)).subscribe({
      next: (data: any) => {
        let user: any = {
          id: this.selectedUser!.id,
          firstName: this.selectedUser!.voornaam,
          prefix: this.selectedUser!.tussenvoegsels,
          lastName: this.selectedUser!.achternaam,
          email: this.selectedUser!.email,
          mobile: this.selectedUser!.telefoon
        }
        this.workgroupMembers.push({ user: user, clearanceLevel: workgroupMember.ClearanceLevel });
        if (this.selectedUser!.id === this.authenticationService.currentUserValue.sub) {
          this.authenticationService.workgroups.push({ Name: this.workgroupName, Guid: this.workgroupGuid, Clearancelevel: workgroupMember.ClearanceLevel });
          this.authenticationService.forceNewToken().subscribe((data) => {
            this.platformHub.start(true);
          })
        }
        (document.getElementById('MemberToAdd') as HTMLInputElement).value = "";
        this.selectedUser = null;
      },
      error: () => {
      }
    });
  }

}
